import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { ResponsiveContainer, PieChart as RPie, Pie, Cell, Tooltip, Legend } from 'recharts';
import { useTheme } from '../../theme/ThemeContext';
import TimeSeriesChart from './TimeSeriesChart';

function leadsLabel(n) {
  const k = Math.abs(n) % 100;
  if (k > 10 && k < 20) return 'лидов';
  const k1 = n % 10;
  if (k1 === 1) return 'лид';
  if (k1 >= 2 && k1 <= 4) return 'лида';
  return 'лидов';
}

function formatLegendAbsolute(v) {
  if (typeof v !== 'number' || Number.isNaN(v)) return String(v ?? '');
  const rounded = Math.round(v);
  if (Math.abs(v - rounded) < 1e-6) {
    return rounded.toLocaleString('ru-RU');
  }
  return v.toLocaleString('ru-RU', { maximumFractionDigits: 2 });
}

function PieTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const p = payload[0];
  const name = p.name ?? p.payload?.name;
  const value = p.value;
  const total = p.payload?.totalForPct;
  const pct = total && value != null ? ((Number(value) / total) * 100).toFixed(1) : null;
  const lc = p.payload?.leadCount;
  const leadsLine =
    typeof lc === 'number' && lc >= 0 ? (
      <div style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 4 }}>
        {lc.toLocaleString('ru-RU')} {leadsLabel(lc)}
      </div>
    ) : null;
  return (
    <div
      style={{
        background: 'var(--bg-card)',
        border: '1px solid var(--border)',
        borderRadius: 8,
        padding: '10px 14px',
        boxShadow: '0 8px 24px rgba(0,0,0,.35)',
      }}
    >
      <div style={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: 13, marginBottom: 4 }}>{name}</div>
      <div style={{ color: 'var(--text-primary)', fontSize: 14, fontVariantNumeric: 'tabular-nums' }}>
        {typeof value === 'number' ? value.toLocaleString('ru-RU') : value}
        {pct != null && <span style={{ color: 'var(--text-muted)', marginLeft: 8 }}>({pct}%)</span>}
      </div>
      {leadsLine}
    </div>
  );
}

export default function PieChartComponent({
  data,
  dataKey = 'value',
  nameKey = 'name',
  height = 420,
  dynamicsMap = null,
}) {
  const { theme } = useTheme();
  const hasDynamics = dynamicsMap != null;
  const [dynModal, setDynModal] = useState(null);

  const totalSum = useMemo(
    () => (data || []).reduce((s, d) => s + (Number(d[dataKey]) || 0), 0),
    [data, dataKey],
  );

  const dataWithTotal = useMemo(
    () => (data || []).map((d) => ({ ...d, totalForPct: totalSum || 1 })),
    [data, totalSum, dataKey],
  );

  const openDynamics = useCallback(
    (payload, segmentColor) => {
      if (!hasDynamics || !payload) return;
      const name = payload[nameKey];
      const metric = payload.dynamicsMetric === 'leads' ? 'leads' : 'revenue';
      const series = dynamicsMap[name];
      const color = segmentColor || 'var(--accent)';
      const chartData = Array.isArray(series) ? series : [];
      const lines =
        metric === 'leads'
          ? [{ key: 'leads', name: 'Лиды', color }]
          : [{ key: 'revenue', name: 'Выручка, ₽', color }];
      const hint =
        metric === 'leads'
          ? 'Помесячное число лидов по каналу (выгрузки AMO).'
          : 'Помесячная выручка по источнику (данные Noviu).';
      setDynModal({ name, metric, chartData, lines, hint });
    },
    [hasDynamics, dynamicsMap, nameKey],
  );

  useEffect(() => {
    if (!dynModal) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setDynModal(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [dynModal]);

  if (!data || data.length === 0) {
    return (
      <div
        style={{
          color: 'var(--text-muted)',
          padding: '32px 20px',
          textAlign: 'center',
          fontSize: 13,
          lineHeight: 1.5,
          borderRadius: 12,
          background: 'var(--bg-input)',
          border: '1px dashed var(--border)',
        }}
      >
        Нет данных за выбранный период или по фильтру компании.
        <br />
        <span style={{ fontSize: 12, opacity: 0.85 }}>Смените период или фильтр компании — данные подгрузятся сами.</span>
      </div>
    );
  }

  const renderLegend = (props) => {
    const { payload } = props;
    if (!payload?.length) return null;
    const t = totalSum || 1;
    return (
      <div
        style={{
          maxHeight: 200,
          overflowY: 'auto',
          padding: '12px 8px 0',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
          gap: '8px 14px',
          fontSize: 12,
          color: 'var(--text-secondary)',
          textAlign: 'left',
        }}
      >
        {payload.map((entry, i) => {
          const v = Number(entry.payload?.[dataKey] ?? entry.value ?? 0);
          const pct = ((v / t) * 100).toFixed(0);
          const lc = entry.payload?.leadCount;
          const showLeadsExtra =
            typeof lc === 'number' && lc >= 0 && Math.round(lc) !== Math.round(v);
          const leadsSuffix = showLeadsExtra
            ? ` · ${lc.toLocaleString('ru-RU')} ${leadsLabel(lc)}`
            : '';
          const absStr = formatLegendAbsolute(v);
          const clickable = hasDynamics;
          return (
            <div
              key={`${entry.value}-${i}`}
              role={clickable ? 'button' : undefined}
              tabIndex={clickable ? 0 : undefined}
              onClick={() => clickable && openDynamics(entry.payload, entry.color)}
              onKeyDown={(e) => {
                if (!clickable) return;
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  openDynamics(entry.payload, entry.color);
                }
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                minWidth: 0,
                cursor: clickable ? 'pointer' : 'default',
                borderRadius: 8,
                padding: clickable ? '4px 6px' : 0,
                margin: clickable ? '-4px -6px' : 0,
                outline: 'none',
                transition: 'background 0.15s ease',
              }}
              onMouseEnter={(e) => {
                if (clickable) e.currentTarget.style.background = 'rgba(34,211,238,.08)';
              }}
              onMouseLeave={(e) => {
                if (clickable) e.currentTarget.style.background = 'transparent';
              }}
            >
              <span style={{ width: 10, height: 10, borderRadius: 2, background: entry.color, flexShrink: 0 }} />
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={entry.value}>
                {entry.value}
              </span>
              <span style={{ color: 'var(--text-muted)', flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>
                {absStr} ({pct}%){leadsSuffix}
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  const handlePieSectorClick = (sector) => {
    const payload = sector?.payload;
    if (!payload || !hasDynamics) return;
    const idx = dataWithTotal.findIndex((d) => d[nameKey] === payload[nameKey]);
    const color = theme.chartColors[(idx >= 0 ? idx : 0) % theme.chartColors.length];
    openDynamics(payload, color);
  };

  return (
    <>
      <ResponsiveContainer width="100%" height={height}>
        <RPie margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
          <Pie
            data={dataWithTotal}
            dataKey={dataKey}
            nameKey={nameKey}
            cx="50%"
            cy="42%"
            innerRadius={52}
            outerRadius={88}
            paddingAngle={0.5}
            label={false}
            stroke={'var(--bg)'}
            strokeWidth={1}
            onClick={handlePieSectorClick}
            cursor={hasDynamics ? 'pointer' : 'default'}
          >
            {dataWithTotal.map((_, i) => (
              <Cell key={i} fill={theme.chartColors[i % theme.chartColors.length]} />
            ))}
          </Pie>
          <Tooltip content={<PieTooltip />} />
          <Legend content={renderLegend} verticalAlign="bottom" />
        </RPie>
      </ResponsiveContainer>
      {hasDynamics && (
        <div
          style={{
            color: 'var(--text-muted)',
            fontSize: 11,
            marginTop: 8,
            textAlign: 'center',
            lineHeight: 1.45,
          }}
        >
          Нажмите на параметр в легенде или на сектор диаграммы, чтобы открыть динамику по месяцам.
        </div>
      )}

      {dynModal && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="pie-dynamics-title"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 10050,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 24,
            background: 'rgba(6,10,20,.72)',
            backdropFilter: 'blur(6px)',
          }}
          onClick={() => setDynModal(null)}
        >
          <div
            style={{
              width: 'min(920px, 100%)',
              maxHeight: 'min(560px, 90vh)',
              overflow: 'auto',
              background: 'var(--surface-sheen), var(--bg-card)',
              border: '1px solid var(--border)',
              borderRadius: 16,
              padding: '22px 24px 20px',
              boxShadow: '0 24px 64px rgba(0,0,0,.45)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 12 }}>
              <div>
                <h3 id="pie-dynamics-title" style={{ color: 'var(--text-primary)', fontSize: 17, fontWeight: 700, margin: 0 }}>
                  Динамика: {dynModal.name}
                </h3>
                <div style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 6 }}>{dynModal.hint}</div>
              </div>
              <button
                type="button"
                aria-label="Закрыть"
                onClick={() => setDynModal(null)}
                style={{
                  flexShrink: 0,
                  background: 'var(--bg-input)',
                  border: '1px solid var(--border)',
                  color: 'var(--text-secondary)',
                  borderRadius: 10,
                  width: 36,
                  height: 36,
                  cursor: 'pointer',
                  fontSize: 18,
                  lineHeight: 1,
                }}
              >
                ×
              </button>
            </div>
            {!dynModal.chartData?.length ? (
              <div style={{ color: 'var(--text-muted)', padding: '28px 8px', textAlign: 'center', fontSize: 13 }}>
                Нет помесячных данных для этого параметра за выбранный период.
              </div>
            ) : (
              <TimeSeriesChart data={dynModal.chartData} lines={dynModal.lines} height={320} />
            )}
          </div>
        </div>
      )}
    </>
  );
}
